import MainCreationForm from "components/forms/MainCreationForm";
import { Button, Form, Input, InputNumber, Select } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { countryCodes } from "assets/data/countryCodes";
import {
  filterOptionInsensitive,
  inputCurrencyFormatter,
  inputCurrencyParser,
} from "utils/functions";
import TimezonePickerField from "components/fields/TimezonePickerField";
import OrganizationSelect from "components/data/OrganizationSelect";
import ImageUploadField from "components/fields/ImageUploadField";

import { VenueFormProps } from "./types";
import { useVenueFormPriceDisplayOptions } from "./hooks";

const VenueForm: React.FC<React.PropsWithChildren<VenueFormProps>> = ({
  loading,
  autodetectTimezone,
  children,
  ...props
}) => {
  const { $isReadOnly: readOnly } = props;
  const intl = useIntl();
  const priceDisplayTypes = useVenueFormPriceDisplayOptions();

  return (
    <MainCreationForm
      name="venueForm"
      autoComplete="off"
      disabled={loading}
      {...props}
    >
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Name",
        })}
        name={["input", "name"]}
        required
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "Venue name",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Venue logo",
        })}
        name={["input", "image"]}
      >
        <ImageUploadField />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Platform Fee",
        })}
        name={["input", "platformFee"]}
        required
      >
        <InputNumber
          {...{ readOnly }}
          formatter={inputCurrencyFormatter}
          parser={inputCurrencyParser}
          precision={2}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Venue Address",
        })}
        name={["input", "address", "address1"]}
        required
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "Venue address first line",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Address Line 2",
        })}
        name={["input", "address", "address2"]}
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "Venue address second line",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "City",
        })}
        name={["input", "address", "city"]}
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "City",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Country",
        })}
        name={["input", "address", "country"]}
        required
      >
        <Select
          disabled={readOnly}
          labelInValue
          showSearch
          placeholder="Select a country"
          filterOption={filterOptionInsensitive}
          options={countryCodes}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "State",
        })}
        name={["input", "address", "province"]}
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "State",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "State Abbreviation",
        })}
        name={["input", "address", "provinceCode"]}
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "State Abbreviation",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Zip Code",
        })}
        name={["input", "address", "zip"]}
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "Zip/post code",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Stripe Account ID",
        })}
        name={["input", "stripeAccountId"]}
      >
        <Input
          {...{ readOnly }}
          placeholder={intl.formatMessage({
            defaultMessage: "Stripe Account",
          })}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Organization",
        })}
        name={["input", "organizationId"]}
        required
      >
        <OrganizationSelect disabled={readOnly} />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Timezone",
        })}
        name={["input", "timezone"]}
      >
        <TimezonePickerField
          disabled={readOnly}
          autodetect={autodetectTimezone}
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          defaultMessage: "Price display type",
        })}
        name={["input", "priceDisplay"]}
        required
      >
        <Select disabled={readOnly} options={priceDisplayTypes} />
      </Form.Item>
      {!readOnly && (
        <Form.Item>
          <Button type="primary" htmlType="submit" {...{ loading }}>
            <FormattedMessage defaultMessage="Submit" />
          </Button>
        </Form.Item>
      )}
      {children}
    </MainCreationForm>
  );
};

export default VenueForm;
